import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Users, Phone, ChevronRight, ChevronDown, CheckCircle2, MessageCircle, Music, Snowflake, Luggage, Wifi, Star
} from "lucide-react";
import PageTransition from "../../components/PageTransition";
import PublicHeader from "../../components/PublicHeader";
import SeoFooter from "../../components/SeoFooter";

const MinibusCharterPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const buses = [
    {
      name: "Mercedes V-Class People Mover",
      capacity: "Up to 6 passengers",
      price: "From $125",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/mercedes-benz-v-250-1-1.png",
      features: ["First-class leather comfort", "Perfect for families & execs", "Generous luggage space"]
    },
    {
      name: "Mercedes Sprinter 11 Seater",
      capacity: "Up to 11 passengers",
      price: "From $165",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png",
      features: ["Climate control & Bluetooth", "USB charging", "Ideal for mid-size groups"]
    },
    {
      name: "Mercedes Sprinter 15 Seater",
      capacity: "Up to 15 passengers",
      price: "From $220",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png",
      features: ["Big group mover", "Huge luggage capacity", "Weddings, cruises & events"]
    },
    {
      name: "Toyota Coaster 21 Seater",
      capacity: "Up to 21 passengers",
      price: "From $170",
      image: "/uploads/vehicles/toyota-coaster-21.jpeg",
      features: ["Comfortable mid-size coach", "Perfect for tours & shuttles", "Climate control throughout"]
    },
    {
      name: "Luxury Coach 53 Seater",
      capacity: "Up to 53 passengers",
      price: "From $180",
      image: "/uploads/vehicles/f8c29278-e300-408e-a17c-d6708bda6890.png",
      features: ["Full-size luxury coach", "Underfloor luggage storage", "Conferences, weddings & events"]
    }
  ];

  const occasions = [
    { title: "Group Airport Transfers", desc: "The whole crew, one bus, one fixed price. Cheaper per person than multiple taxis, and everyone arrives together." },
    { title: "Cruise Terminal Groups", desc: "Direct runs to the Brisbane International Cruise Terminal with room for every suitcase." },
    { title: "Weddings & Guest Shuttles", desc: "Shuttle loops between the ceremony, reception and accommodation so no guest drives." },
    { title: "Winery & Brewery Tours", desc: "Mount Tamborine, Scenic Rim and Sunshine Coast hinterland day charters with your own driver." },
    { title: "Hens & Bucks Parties", desc: "Your playlist on Bluetooth, venue-to-venue transfers and safe late-night returns." },
    { title: "Corporate & Team Events", desc: "Conference shuttles, team days and client events — one invoice, zero logistics stress." },
    { title: "Concerts & Stadium Events", desc: "Suncorp Stadium, entertainment centres and festivals — beat the parking, travel together." },
    { title: "School & Sports Groups", desc: "Reliable, safety-first group transport for excursions, sports teams and formals." }
  ];

  const faqs = [
    { q: "How much does minibus charter with a driver cost?", a: "Point-to-point transfers start from $165 for our 11-seater and $220 for the 15-seater. Day charters (wineries, weddings, events) are quoted by hours and distance — most day rates start around $650–$800. Get an exact quote online or via WhatsApp in minutes." },
    { q: "Do all charters include the driver?", a: "Yes — every Limo Near Me charter includes a professional, licensed chauffeur. We never do self-drive; you relax, we drive." },
    { q: "How many passengers can you carry?", a: "Up to 53 in a single luxury coach, 21 in our Toyota Coaster and 15 in a Mercedes Sprinter. Bigger group? We run multiple vehicles in convoy — 100, 200 or more guests travelling together on the same schedule." },
    { q: "Can we play our own music?", a: "Absolutely — Bluetooth audio in every minibus. Your bus, your playlist." },
    { q: "Is there room for luggage, prams or golf clubs?", a: "Yes. Our Sprinters carry substantial luggage — cruise cases, golf bags, prams and even surfboards. Just tell us what you're bringing when booking." },
    { q: "How far in advance should we book a minibus?", a: "Airport transfers can often be booked same-day, but for Saturday weddings, hens parties and event dates we recommend 2–4 weeks ahead." }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: "Minibus Charter with Driver",
        name: "Minibus Charter & Group Transfers - Limo Near Me",
        description: "Mercedes Sprinter minibus hire with professional driver across Brisbane, Gold Coast and South East Queensland. Group airport transfers, weddings, winery tours, hens and bucks parties.",
        provider: { "@type": "Organization", name: "Limo Near Me", telephone: "+61481437772", url: "https://www.limonearme.com.au" },
        areaServed: ["Brisbane", "Gold Coast", "Sunshine Coast", "Ipswich", "Byron Bay"]
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } }))
      }
    ]
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background" data-testid="minibus-charter-page">
        <Helmet>
          <title>Minibus Charter with Driver | Mercedes Sprinter Hire Brisbane & Gold Coast | Limo Near Me</title>
          <meta name="description" content="Minibus charter and Mercedes Sprinter hire with professional driver across Brisbane, Gold Coast & SEQ. Group airport transfers, weddings, winery tours, hens & bucks parties. 11 & 15 seaters, fixed prices. Book online 24/7." />
          <meta name="keywords" content="minibus charter Brisbane, minibus hire with driver, Mercedes Sprinter hire, group airport transfer, party bus Gold Coast, 15 seater bus hire, winery tour bus" />
          <link rel="canonical" href="https://www.limonearme.com.au/services/minibus-charter" />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>

        <PublicHeader />

        {/* Hero */}
        <section className="relative pt-24 min-h-[75vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/9201fb6d-e664-4f66-b16d-b383a95202f6/images/26c02035ac26d02ccf15b201aa8d76d5a74fd1fecfec00efb2ae24df30948a9a.jpeg')" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/20 text-amber-300 mb-6 backdrop-blur-sm">
                <Users className="h-4 w-4" />
                <span className="text-sm font-medium">Groups of 6 to 200+ · Driver always included</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
                Minibus & Coach Charter<br /><span className="text-amber-400">With Your Own Driver</span>
              </h1>
              <p className="text-lg text-gray-200 mb-8">
                Mercedes Sprinter minibuses, 21-seat Toyota Coasters and 53-seat luxury coaches for group airport
                transfers, weddings, winery tours, hens & bucks parties and corporate days across Brisbane, the
                Gold Coast and South East Queensland. One vehicle or a whole convoy — one fixed price, everyone together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg font-bold" data-testid="minibus-book-btn">
                    Get a Minibus Quote <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/61481437772?text=Hi!%20I'd%20like%20a%20minibus%20charter%20quote." target="_blank" rel="noreferrer">
                  <Button size="lg" className="rounded-full px-8 py-6 text-lg bg-green-600 hover:bg-green-700 text-white">
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp a Quote
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Buses */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Our Group Fleet</h2>
            <p className="text-muted-foreground text-center mb-12">Immaculate vehicles from 6 to 53 seats, professionally driven, priced upfront</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {buses.map((bus, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-xl hover:border-amber-300 transition-all">
                  <div className="h-48 bg-gradient-to-br from-amber-50 to-gray-50 flex items-center justify-center p-6">
                    <img src={bus.image} alt={bus.name} className="max-h-full object-contain" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-1">{bus.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{bus.capacity}</p>
                    <ul className="space-y-2 mb-4">
                      {bus.features.map((f, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-amber-600 flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-amber-700">Instant price at booking</span>
                      <Link to="/book"><Button size="sm" className="rounded-full">Book</Button></Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-gray-950 text-white rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold mb-2 text-amber-400">More than 53 passengers?</h3>
              <p className="text-gray-300 mb-4">No problem — we run multi-vehicle convoys so groups of 100, 200 or more travel together on the same schedule.</p>
              <a href="https://wa.me/61481437772?text=Hi!%20I%20need%20transport%20for%20a%20large%20group." target="_blank" rel="noreferrer">
                <Button className="rounded-full px-8">Plan a Large Group Move</Button>
              </a>
            </div>
          </div>
        </section>

        {/* Comfort strip */}
        <section className="py-12 bg-amber-50">
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
            {[
              { icon: Music, label: "Bluetooth audio" },
              { icon: Snowflake, label: "Climate control" },
              { icon: Luggage, label: "Big luggage space" },
              { icon: Wifi, label: "USB charging" },
              { icon: Star, label: "Professional drivers" }
            ].map((f, i) => (
              <div key={i}>
                <f.icon className="h-7 w-7 text-amber-600 mx-auto mb-2" />
                <p className="text-sm font-medium">{f.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Occasions */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">What Do You Need a Minibus For?</h2>
            <p className="text-muted-foreground text-center mb-12">We've done every kind of group day out, hundreds of times</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {occasions.map((o, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="bg-white rounded-xl p-6 border border-black/5 hover:border-amber-300 hover:shadow-lg transition-all">
                  <h3 className="font-semibold mb-2">{o.title}</h3>
                  <p className="text-sm text-muted-foreground">{o.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Minibus Charter FAQs</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl border border-black/10 p-5">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between text-left" data-testid={`minibus-faq-${index}`}>
                    <h3 className="font-semibold pr-4">{faq.q}</h3>
                    <ChevronDown className={`h-5 w-5 text-amber-600 transition-transform flex-shrink-0 ${openFaq === index ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === index && <p className="text-muted-foreground text-sm mt-4 pt-4 border-t border-black/10">{faq.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950 text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4">Get Your Group Moving</h2>
            <p className="text-gray-400 mb-8">Tell us the date, group size and pickup — your fixed quote comes back fast.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book"><Button size="lg" className="rounded-full px-10 py-6 text-lg font-bold" data-testid="minibus-cta-book-btn">Book Online Now</Button></Link>
              <a href="tel:+61481437772"><Button size="lg" variant="outline" className="rounded-full px-10 py-6 text-lg border-white/30 text-white hover:bg-white/10"><Phone className="mr-2 h-5 w-5" /> +61 481 437 772</Button></a>
            </div>
          </div>
        </section>

        <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default MinibusCharterPage;
